// ==================== GURUKUL — RETIRE (soft delete) ====================
// Coordinators ask to retire a learner/parent in their team; a Gurukul admin approves (or retires
// directly) and can reinstate. Nothing is deleted. Same card/table/mobile classes as the rest.
import { showToast, showConfirm } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}
const post = (path, body) => api('/api/gurukul/retire', { method: 'POST', body: JSON.stringify(body) });

let isManager = false;
const REASON_LABEL = { graduated: 'Graduated', left: 'Left', other: 'Other' };

export function retireSectionHtml(manager) {
  return `
    <div class="card" id="gk-sec-retire">
      <h2>${manager ? 'Retire learners & parents' : 'My team — retire'}</h2>
      <p class="page-intro">Retiring keeps every record${manager ? '' : '; a Gurukul admin approves your request'}. A parent can only be retired once every active child has another guardian (and another primary).</p>
      <h3>Learners</h3>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Learner</th><th>Guardians</th><th>Status</th><th></th></tr></thead>
        <tbody id="gkRetLearners"><tr><td colspan="4" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
      <h3 style="margin-top:16px;">Parents</h3>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
        <tbody id="gkRetParents"><tr><td colspan="3" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
    </div>
    ${manager ? `<div class="card" id="gk-sec-retired">
      <h2>Retired</h2>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Who</th><th>Type</th><th>Reason</th><th>Since</th><th></th></tr></thead>
        <tbody id="gkRetired"><tr><td colspan="5" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
    </div>` : ''}
  `;
}

function askForm({ title, intro, withReason, reasonRequired, confirmLabel }) {
  return new Promise(resolve => {
    let modal = document.getElementById('gkRetModal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'gkRetModal';
      modal.className = 'modal';
      document.body.appendChild(modal);
    }
    modal.innerHTML = `<div class="modal-content" style="max-width:480px;padding:24px 30px;">
      <h2>${esc(title)}</h2><p class="page-intro">${esc(intro || '')}</p>
      ${withReason ? `<div class="form-group"><label for="gkRetReason">Reason</label><select id="gkRetReason">${Object.entries(REASON_LABEL).map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select></div>` : ''}
      <div class="form-group"><label for="gkRetNote">${reasonRequired ? 'Reason (required)' : 'Note (optional)'}</label><textarea id="gkRetNote" rows="3"></textarea></div>
      <div class="btn-group"><button type="button" id="gkRetOk">${esc(confirmLabel)}</button><button type="button" class="secondary" id="gkRetCancel">Cancel</button></div></div>`;
    modal.classList.add('active');
    const done = v => { modal.classList.remove('active'); resolve(v); };
    document.getElementById('gkRetCancel').addEventListener('click', () => done(null));
    document.getElementById('gkRetOk').addEventListener('click', () => {
      const note = document.getElementById('gkRetNote').value.trim();
      if (reasonRequired && !note) { showToast('Please give a reason', 'error'); return; }
      done({ reason: withReason ? document.getElementById('gkRetReason').value : undefined, note });
    });
  });
}
const askReason = () => askForm({ title: 'Retire', intro: 'All records are kept. Coordinators\' requests go to a Gurukul admin for approval.', withReason: true, confirmLabel: 'Retire' });

async function doRetire(kind, id) {
  const r = await askReason();
  if (!r) return;
  try {
    const out = await post('/api/gurukul/retire', { action: 'retire', kind, target_id: id, ...r });
    showToast(out.status === 'pending' ? 'Request sent to the Gurukul admin — track it under Requests' : 'Retired — records kept', 'success');
    await loadAll();
  } catch (err) { showToast(err.message, 'error'); }
}

async function loadAll() {
  const mine = await api('/api/gurukul/retire?view=my-team').catch(e => ({ error: e.message }));
  const lb = document.getElementById('gkRetLearners');
  const pb = document.getElementById('gkRetParents');
  if (mine.error) {
    lb.innerHTML = pb.innerHTML = `<tr><td colspan="4" class="empty-state">Failed to load: ${esc(mine.error)}</td></tr>`;
  } else {
    lb.innerHTML = mine.learners.length ? mine.learners.map(l => `
      <tr><td data-label="Learner">${esc(l.learner_id)}</td>
        <td data-label="Guardians">${l.guardians.map(g => esc(g.name) + (g.is_primary ? ' ★' : '')).join(', ') || '—'}</td>
        <td data-label="Status">${l.status === 'retired' ? 'Retired (' + esc(l.retire_reason || '') + ')' : 'Active'}</td>
        <td data-label="" class="action-buttons">${l.status === 'retired' ? '' : `<button type="button" class="gk-ret-btn" data-kind="learner" data-id="${l.id}">Retire</button>`}</td></tr>`).join('')
      : '<tr><td colspan="4" class="empty-state">No learners.</td></tr>';
    pb.innerHTML = mine.parents.length ? mine.parents.map(p => `
      <tr><td data-label="Name">${esc(p.name)}</td><td data-label="Email">${esc(p.email)}</td>
        <td data-label="" class="action-buttons"><button type="button" class="gk-ret-btn" data-kind="parent" data-id="${p.id}">Retire</button></td></tr>`).join('')
      : '<tr><td colspan="3" class="empty-state">No parents.</td></tr>';
    document.querySelectorAll('#gk-sec-retire .gk-ret-btn').forEach(b => b.addEventListener('click', () => doRetire(b.dataset.kind, b.dataset.id)));
  }

  if (isManager) {
    const tb = document.getElementById('gkRetired');
    try {
      const { retired } = await api('/api/gurukul/retire?view=retired');
      tb.innerHTML = retired.length ? retired.map(r => `
        <tr><td data-label="Who">${esc(r.learner_code || r.person || '—')}</td><td data-label="Type">${esc(r.kind)}</td>
          <td data-label="Reason">${esc(REASON_LABEL[r.reason] || r.reason)}</td><td data-label="Since">${new Date(r.created_at).toLocaleDateString()}</td>
          <td data-label="" class="action-buttons"><button type="button" class="gk-ret-back" data-id="${r.id}">Reinstate</button></td></tr>`).join('')
        : '<tr><td colspan="5" class="empty-state">Nobody retired.</td></tr>';
      tb.querySelectorAll('.gk-ret-back').forEach(b => b.addEventListener('click', async () => {
        if (!(await showConfirm('Reinstate this person? Their access and records are restored.'))) return;
        try { await post(null, { action: 'reinstate', retirement_id: b.dataset.id }); showToast('Reinstated', 'success'); await loadAll(); } catch (e) { showToast(e.message, 'error'); }
      }));
    } catch (e) { tb.innerHTML = `<tr><td colspan="5" class="empty-state">Failed to load: ${esc(e.message)}</td></tr>`; }
  }
}

export async function initRetireSection(manager) {
  isManager = !!manager;
  await loadAll();
}
